import React from "react";
import { Card } from "antd";
import {
  WarningOutlined,
  DollarOutlined,
  CreditCardOutlined,
  LineChartOutlined,
  BarChartOutlined,
  TrophyOutlined,
  BankOutlined,
  SwapOutlined,
  AlertOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";

import SpendingChart from "./charts/SpendingChart";
import DebtCoverage from "./charts/DebtCoverage";
import { IntelligenceDto } from "dtos/application.dto";

interface Props {
  data: IntelligenceDto["behavioral_analysis"];
}

const RiskBadge = ({ level }: { level: string }) => {
  const lower = level?.toLowerCase();
  const config =
    lower === "high" || lower?.includes("critical")
      ? { bg: "bg-red-50", text: "text-red-600", dot: "bg-red-500" }
      : lower === "medium" || lower === "moderate"
        ? { bg: "bg-orange-50", text: "text-orange-500", dot: "bg-orange-500" }
        : lower === "low"
          ? { bg: "bg-green-50", text: "text-green-600", dot: "bg-green-500" }
          : { bg: "bg-gray-100", text: "text-gray-500", dot: "bg-gray-400" };

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${config.bg} ${config.text}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
      {level?.toUpperCase() || "UNKNOWN"}
    </span>
  );
};

const StatRow = ({
  label,
  value,
  sub,
  icon,
  iconColor,
}: {
  label: string;
  value: string;
  sub?: string;
  icon: React.ReactNode;
  iconColor: string;
}) => (
  <div className="flex items-center gap-3 py-2.5 border-b border-gray-100 last:border-0">
    <div
      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
      style={{ backgroundColor: `${iconColor}18`, color: iconColor }}
    >
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs text-gray-400">{label}</p>
      <p className="text-sm font-semibold text-gray-800 truncate">{value}</p>
    </div>
    {sub && <span className="text-xs text-gray-400 flex-shrink-0">{sub}</span>}
  </div>
);

const BehavioralCharts = ({ data }: Props) => {
  const highRiskTxns = data.high_risk_transactions?.transactions ?? [];

  return (
    <div className="p-4 min-h-screen space-y-6">
      {/* ── Top 3 KPI Cards ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Liquidity & Savings */}
        <Card
          className="border border-gray-300"
          title={
            <div className="flex items-center gap-2">
              <DollarOutlined style={{ color: "#22c55e" }} />
              <span className="font-semibold text-gray-800 text-sm">Liquidity & Savings</span>
            </div>
          }
          extra={<RiskBadge level={data.liquidity_ratio?.risk_level || "Low"} />}
        >
          <StatRow
            label="Liquid Assets"
            value={`$${data.liquidity_ratio?.liquid_assets?.toLocaleString() || "0"}`}
            sub="Available"
            icon={<DollarOutlined />}
            iconColor="#22c55e"
          />
          <StatRow
            label="Monthly Obligations"
            value={`$${data.liquidity_ratio?.monthly_obligations?.toLocaleString() || "0"}`}
            icon={<CreditCardOutlined />}
            iconColor="#ef4444"
          />
          <StatRow
            label="Liquidity Ratio"
            value={`${data.liquidity_ratio?.ratio?.toFixed(2) || "0"}x`}
            sub="Coverage"
            icon={<LineChartOutlined />}
            iconColor="#3b82f6"
          />
          <StatRow
            label="Savings Transfers"
            value={data.savings_behavior?.total_transfers?.toLocaleString() || "0"}
            sub={`$${data.savings_behavior?.total_value?.toLocaleString() || "0"} total`}
            icon={<SwapOutlined />}
            iconColor="#8b5cf6"
          />
        </Card>

        <Card
          className="border border-gray-300"
          title={
            <div className="flex items-center gap-2">
              <WarningOutlined style={{ color: "#f97316" }} />
              <span className="font-semibold text-gray-800 text-sm">NSF & Fabrication</span>
            </div>
          }
          extra={<RiskBadge level={data.transaction_fabrication?.risk_level || "Low"} />}
        >
          <StatRow
            label="NSF Events"
            value={data.nsf_metrics?.total_count?.toLocaleString() || "0"}
            sub="occurrences"
            icon={<AlertOutlined />}
            iconColor="#ef4444"
          />
          <StatRow
            label="NSF Total Fees"
            value={`$${data.nsf_metrics?.total_fees?.toLocaleString() || "0"}`}
            icon={<CreditCardOutlined />}
            iconColor="#f97316"
          />
          <StatRow
            label="Fabricated Transactions"
            value={data.transaction_fabrication?.total_loop_count?.toLocaleString() || "0"}
            sub="loops detected"
            icon={<BarChartOutlined />}
            iconColor="#8b5cf6"
          />
          <StatRow
            label="High Risk Transactions"
            value={data.high_risk_transactions?.total_count?.toLocaleString() || "0"}
            sub={`$${Math.abs(data.high_risk_transactions?.total_value ?? 0).toLocaleString()} value`}
            icon={<ExclamationCircleOutlined />}
            iconColor="#dc2626"
          />
        </Card>

        <Card
          className="border border-gray-300"
          title={
            <div className="flex items-center gap-2">
              <TrophyOutlined style={{ color: "#3b82f6" }} />
              <span className="font-semibold text-gray-800 text-sm">Income & Debt</span>
            </div>
          }
          extra={<RiskBadge level={data.debt_coverage?.risk_level || "Low"} />}
        >
          <StatRow
            label="Avg Monthly Income"
            value={`$${data.debt_coverage?.average_monthly_income?.toLocaleString() || "0"}`}
            icon={<DollarOutlined />}
            iconColor="#22c55e"
          />
          <StatRow
            label="Avg Monthly Debt"
            value={`$${data.debt_coverage?.average_monthly_debt?.toLocaleString() || "0"}`}
            icon={<CreditCardOutlined />}
            iconColor="#ef4444"
          />
          <StatRow
            label="Debt Coverage Ratio"
            value={`${data.debt_coverage?.average_debt_coverage_ratio?.toFixed(2) || "0.00"}x`}
            sub={data.debt_coverage?.average_debt_coverage_ratio >= 1 ? "✓ Covered" : "✗ Undercovered"}
            icon={<LineChartOutlined />}
            iconColor="#3b82f6"
          />
          <StatRow
            label="Withdrawal Activity"
            value={data.withdrawal_analysis?.total_count?.toLocaleString() || "0"}
            sub={`$${data.withdrawal_analysis?.total_value?.toLocaleString() || "0"} total`}
            icon={<BankOutlined />}
            iconColor="#f59e0b"
          />
        </Card>
      </div>

      {highRiskTxns.length > 0 && (
        <Card
          className="border border-gray-300"
          title={
            <div className="flex items-center gap-2">
              <ExclamationCircleOutlined style={{ color: "#ef4444" }} />
              <span className="font-semibold text-gray-800 text-sm">High Risk Transactions</span>
              <span className="ml-1 text-xs bg-red-50 text-red-500 px-2 py-0.5 rounded-full">
                {highRiskTxns.length} flagged
              </span>
            </div>
          }
        >
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-gray-600">
              <thead>
                <tr className="border-b border-gray-100 text-gray-400">
                  <th className="text-left pb-2 font-medium">Date</th>
                  <th className="text-left pb-2 font-medium">Description</th>
                  <th className="text-right pb-2 font-medium">Amount</th>
                </tr>
              </thead>
              <tbody>
                {highRiskTxns.map((txn: any, i: number) => (
                  <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="py-2 text-gray-500 whitespace-nowrap">
                      {new Date(txn["Transaction Date"]).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </td>
                    <td className="py-2 max-w-[260px] truncate" title={txn.Description}>
                      {txn.Description}
                    </td>
                    <td className="py-2 text-right font-semibold text-red-500">
                      ${Math.abs(txn.amount).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-gray-200">
                  <td colSpan={2} className="pt-2 text-xs text-gray-400 font-medium">
                    Total
                  </td>
                  <td className="pt-2 text-right font-bold text-red-500 text-xs">
                    ${Math.abs(data.high_risk_transactions?.total_value ?? 0).toLocaleString()}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SpendingChart spendingData={data.spending_breakdown} />
        <DebtCoverage debtcoverage={data.debt_coverage} />
      </div>
    </div>
  );
};

export default BehavioralCharts;
